import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { authApi, formatApiError } from "@/lib/api";
import { Heart, Phone, ArrowLeft, Key, Lock, EnvelopeSimple, ChatText } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ForgotPasswordPage() {
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get("token") || "";
  const [step, setStep] = useState(tokenFromUrl ? "token" : "phone");
  const [phone, setPhone] = useState("");
  const [resetToken, setResetToken] = useState(tokenFromUrl);
  const [deliveryChannel, setDeliveryChannel] = useState("none"); // 'email' | 'sms' | 'none'
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (tokenFromUrl) toast.info("Open the form below and choose a new password");
  }, [tokenFromUrl]);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setError("");
    if (!phone.trim()) { setError("Enter your phone number"); return; }
    setLoading(true);
    try {
      const { data } = await authApi.forgotPassword(phone.trim());
      setDeliveryChannel(data.channel || "none");
      if (data.resetToken) setResetToken(data.resetToken);
      if (data.channel === "email") toast.success("Check your email for the reset link");
      else if (data.channel === "sms") toast.success("Check your phone — we sent the reset link via SMS");
      else toast.info("Reset code generated — copy it below");
      setStep("token");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (!resetToken.trim()) { setError("Enter the reset code"); return; }
    if (newPassword.length < 4) { setError("Password must be at least 4 characters"); return; }
    if (newPassword !== confirmPassword) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      await authApi.resetPassword(resetToken.trim(), newPassword);
      toast.success("Password reset successfully!");
      setStep("done");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2.5 mb-6">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
              <Heart className="w-5 h-5 text-primary-foreground" weight="fill" />
            </div>
            <span className="font-heading text-2xl font-bold tracking-tighter">Utheri</span>
          </Link>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">
            {step === "done" ? "Password Reset" : "Forgot Password"}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {step === "phone" && "Enter your registered phone number"}
            {step === "token" && "Enter the reset code and your new password"}
            {step === "done" && "Your password has been updated"}
          </p>
        </div>

        {step === "phone" && (
          <form onSubmit={handleRequestReset} className="bg-card rounded-xl border border-border p-6 shadow-sm space-y-4">
            {error && <div className="bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-lg p-3">{error}</div>}
            <div>
              <label className="text-sm font-medium text-foreground block mb-1.5">Phone Number</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input data-testid="forgot-phone-input" type="tel" placeholder="07XX XXX XXX" value={phone} onChange={(e) => setPhone(e.target.value)}
                  className="w-full pl-10 pr-3 py-2.5 text-sm bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring" required />
              </div>
            </div>
            <button data-testid="forgot-submit-btn" type="submit" disabled={loading}
              className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm active:scale-95 transition-transform disabled:opacity-60">
              {loading ? "Sending..." : "Get Reset Code"}
            </button>
          </form>
        )}

        {step === "token" && (
          <form onSubmit={handleResetPassword} className="bg-card rounded-xl border border-border p-6 shadow-sm space-y-4">
            {error && <div className="bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-lg p-3">{error}</div>}

            {deliveryChannel === "email" && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-start gap-2.5">
                <EnvelopeSimple className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" weight="duotone" />
                <p className="text-xs text-emerald-900">A reset link has been emailed. Click the button in your inbox — the code below is auto-filled if you arrived via that link.</p>
              </div>
            )}
            {deliveryChannel === "sms" && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-start gap-2.5">
                <ChatText className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" weight="duotone" />
                <p className="text-xs text-emerald-900">A reset link has been sent via SMS. Tap it on your phone or copy the code below.</p>
              </div>
            )}
            {deliveryChannel === "none" && resetToken && (
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Email/SMS not configured — copy this reset code:</p>
                <p className="font-mono text-sm text-primary font-semibold break-all select-all" data-testid="reset-token-display">{resetToken}</p>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-foreground block mb-1.5">Reset Code</label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input data-testid="reset-token-input" type="text" placeholder="Paste your reset code" value={resetToken} onChange={(e) => setResetToken(e.target.value)}
                  className="w-full pl-10 pr-3 py-2.5 text-sm bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring" required />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground block mb-1.5">New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input data-testid="new-password-input" type="password" placeholder="Enter new password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-10 pr-3 py-2.5 text-sm bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring" required />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground block mb-1.5">Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input data-testid="confirm-password-input" type="password" placeholder="Confirm new password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-10 pr-3 py-2.5 text-sm bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring" required />
              </div>
            </div>
            <button data-testid="reset-submit-btn" type="submit" disabled={loading}
              className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm active:scale-95 transition-transform disabled:opacity-60">
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </form>
        )}

        {step === "done" && (
          <div className="bg-card rounded-xl border border-border p-6 shadow-sm text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto">
              <Key className="w-8 h-8 text-green-600" weight="fill" />
            </div>
            <p className="text-foreground font-medium">Your password has been reset successfully!</p>
            <Link to="/login" data-testid="back-to-login-btn">
              <button className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm active:scale-95 transition-transform">
                Sign In
              </button>
            </Link>
          </div>
        )}

        <div className="text-center mt-4">
          <Link to="/login" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-login-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
}
